import { observer } from 'mobx-react-lite'
import { type ReactNode, type FC } from 'react'
import {
  Dropdown,
  DropdownContent,
  DropdownGroup,
  DropdownItem,
  DropdownTextInput,
  EnumSpinnerLoaderPosition,
  type IDropdownProps,
  Scrollbar,
  SpinnerLoader,
  Typography,
} from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { makeTextFieldClear } from 'shared/ui/TextField'
import { RegisterPath } from 'entities/ExportRouters'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type MultiOrganizationsDropdownStore } from 'widgets/MultiOrganizationsDropdown/store/multiOrganizationsDropdownStore'
import styles from './styles.module.scss'

export interface IMultiOrganizationsDropdownProps {
  store: MultiOrganizationsDropdownStore
  parentsDropdownId?: string[]
  width?: number
  placement?: IDropdownProps['placement']
  triggerComponent: (open: boolean) => ReactNode
  onCloseDropdown?: () => void
}
export const MultiOrganizationsDropdown: FC<IMultiOrganizationsDropdownProps> = observer(
  ({
    store,
    parentsDropdownId,
    triggerComponent,
    placement = 'bottom-start',
    width = 264,
    onCloseDropdown,
  }) => {
    const { eventLogStore, organizationStore, multiOrganizationStore } = useEntitiesContext()
    const { loading } = multiOrganizationStore
    const {
      searchValue,
      setSearchValue,
      items,
      changeActiveOrganization,
      multiOrganisationsCount,
    } = store

    const autoHeightMax = items.length > 3 ? 149 : 133

    const openMultiorgRegister = () => {
      if (uiStore.routeBlocker?.state === 'blocked') return

      uiStore.changeRouteViaVue({
        path: `/${RegisterPath.multiorg}`,
      })
    }

    return (
      <Dropdown
        width={width}
        ariaLabel={'MultiOrganizationsDropdown'}
        placement={placement}
        parentsDropdownId={parentsDropdownId}
        onClose={onCloseDropdown}
        triggerComponent={triggerComponent}
        customComponent={(onClose) => {
          return (
            <DropdownContent noPadding>
              <DropdownTextInput
                textFieldProps={{
                  onChange: setSearchValue,
                  value: searchValue,
                  InputProps: {
                    placeholder: 'Search',
                  },
                  rightActions: [makeTextFieldClear()],
                }}
              />
              {loading && (
                <SpinnerLoader
                  ariaLabel={'MultiOrganizationsDropdown'}
                  position={EnumSpinnerLoaderPosition.Absolute}
                />
              )}
              <Scrollbar autoHeight autoHeightMin={0} autoHeightMax={autoHeightMax}>
                <Typography
                  variant={'body-sm-medium'}
                  ellipsis
                  color={'var(--content-primary-tertiary)'}
                  className={styles.label}
                  ariaLabel={'Organizations'}
                >
                  Organizations
                </Typography>
                <DropdownGroup
                  key={'Organizations'}
                  items={items}
                  isScroll={false}
                  onChange={(value) => {
                    changeActiveOrganization(value, onClose, onCloseDropdown)
                  }}
                />
              </Scrollbar>
              <div className={styles.dropdownItemWrap}>
                <DropdownItem
                  key={'add'}
                  item={{
                    id: 'add',
                    label: 'New organization',
                    iconL: 'add',
                    onClick: () => {
                      const payload = {
                        event_id: 'multi_organisation_used',
                        current_number_of_orgs: multiOrganisationsCount,
                      }

                      eventLogStore.logEvent('Multi Organization Used', payload, {
                        groupId: organizationStore.id,
                      })
                      openMultiorgRegister()
                    },
                  }}
                />
              </div>
            </DropdownContent>
          )
        }}
      />
    )
  }
)
